import { type FC, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TicketBookingBreadcrumb } from './TicketBookingBreadcrumb';
import { Toaster } from '@ui/sonner';
import { cn } from '@shared/lib/utils';
import { useAppDispatch, useAppSelector } from '../store/store';
import { setCurrentStep } from '../navigate/store/slice';
import { AppRoutes } from '../navigate/consts/routes.consts';
import { Loader } from '@shared/Loader/Loader';
import { errorsSelectors, isLoadingSelectors, showInfoSelectors } from '@modules-dateSelection';
import { isSeatResourcesLoadingSelector, urlParamsDataSelectors } from '@modules-seatSelection';
import type { ThemeId } from '../models/themes.type';

const resolveStepFromPath = (path: string): AppRoutes => {
  if (path === '/' || path === AppRoutes.DATE_SELECTION) {
    return AppRoutes.DATE_SELECTION;
  }
  if (path.startsWith(AppRoutes.SEATS_SELECTION)) {
    return AppRoutes.SEATS_SELECTION;
  }
  if (path.startsWith(AppRoutes.CHECKOUT)) {
    return AppRoutes.CHECKOUT;
  }
  return AppRoutes.DATE_SELECTION;
};

interface LayoutProps {
  themeId: ThemeId;
}

export const Layout: FC<LayoutProps> = ({ themeId }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const dateSelectionLoading = useAppSelector(isLoadingSelectors);
  const dateSelectionError = useAppSelector(errorsSelectors);
  const isSeatResourcesLoading = useAppSelector(isSeatResourcesLoadingSelector);
  const showInfo = useAppSelector(showInfoSelectors);
  const urlParamsData = useAppSelector(urlParamsDataSelectors);

  const activeStep = useMemo(() => resolveStepFromPath(location.pathname), [location.pathname]);

  const isDateStep = activeStep === AppRoutes.DATE_SELECTION;
  const isSeatStep = activeStep === AppRoutes.SEATS_SELECTION;

  const isShowInfoReady = Boolean(urlParamsData.showIds && showInfo?.id === urlParamsData.showIds);
  const isDateDataLoading = !dateSelectionError && (dateSelectionLoading || !isShowInfoReady);
  const isSeatDataLoading = isSeatResourcesLoading || !showInfo;
  const showLoader =
    (isDateStep && isDateDataLoading) || (isSeatStep && isSeatDataLoading) || false;

  useEffect(() => {
    dispatch(setCurrentStep(activeStep));
  }, [activeStep, dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-surface-page">
      <div className="container mx-auto px-4 py-3 sm:py-4 max-w-6xl">
        <TicketBookingBreadcrumb />
        <main className="relative min-h-[calc(100vh-200px)]">
          <div
            className={cn(
              showLoader ? 'pointer-events-none opacity-0 absolute inset-0' : 'relative opacity-100'
            )}
          >
            <Outlet />
          </div>
        </main>
        <Toaster themeId={themeId} position="top-center" expand={true} richColors closeButton />
      </div>
      {showLoader && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-page">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default Layout;
